import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceCode, ThirdPartyMessageStatus, ThirdPartyMessageType } from '@common/enums';
import type { IThirdPartyMessagesListSearchState } from '@frontend/components/pages/thirdPartyMessages/ThirdPartyMessagesListSearchHeader';
import { ManagerAuthorization } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { Link, LinkOnClick } from '@frontend/components/elements/common';
import { ListPager } from '@frontend/components/elements/ListPager';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { ThirdPartyMessageResultModal } from '@frontend/components/pages/thirdPartyMessages/ThirdPartyMessageResultModal';
import { ThirdPartyMessagesListSearchHeader } from '@frontend/components/pages/thirdPartyMessages/ThirdPartyMessagesListSearchHeader';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import { useSearch } from '@frontend/utils/hooks/useSearch';

export interface IGetThirdPartyMessages {
    firstScheduledDateTime?: string;
    messageSubtype?: string;
    messageType?: ThirdPartyMessageType;
    status?: ThirdPartyMessageStatus;
}

interface IThirdPartyMessage {
    finishedDateTime: Date | null;
    firstScheduledDateTime: Date;
    id: string;
    messageSubtype: string | null;
    messageType: ThirdPartyMessageType;
    nextScheduledDateTime: Date | null;
    numberOfFailures: number;
    recipient: string | null;
    request: {
        id: string | null;
        processId: string | null;
        service: {
            code: ServiceCode | null;
        };
    };
    status: ThirdPartyMessageStatus;
}

export function ThirdPartyMessagesList() {
    const [resultsMessageId, setResultsMessageId] = useState<string>();
    const [searchState, setSearchState, pushSearchStateToHistory] = useSearch<IThirdPartyMessagesListSearchState>(
        getCleanThirdPartyMessagesSearchState()
    );
    const [thirdPartyMessages, setThirdPartyMessages] = useState<IThirdPartyMessage[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [totalMessages, setTotalMessages] = useState(0);

    assertHasAuthorization(ManagerAuthorization.thirdPartyMessages);
    const canReadProcesses = hasOneAuthorization(ManagerAuthorization.adminProcessRead);

    useEffect(() => {
        loadMessages();
    }, [searchState]);

    return (
        <PageTemplate>
            <ThirdPartyMessagesListSearchHeader
                clearSearchState={clearSearchState}
                handleSearch={handleSearch}
                searchState={searchState}
            />
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('processThirdPartyMessages.process')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.messageType')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.messageSubtype')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.status')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.recipient')}</TableHeader>
                            <TableHeader>
                                {translateKey('processThirdPartyMessages.firstScheduledDateTime')}
                            </TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.nextScheduledDateTime')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.finishedDateTime')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.numberOfFailures')}</TableHeader>
                            <TableHeader>{translateKey('processThirdPartyMessages.results')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {thirdPartyMessages.map((thirdPartyMessage) => (
                            <TableRow key={thirdPartyMessage.id}>
                                <TableCell>
                                    {thirdPartyMessage.request.id && (
                                        <>
                                            {canReadProcesses ? (
                                                <Link to={'/processes/' + thirdPartyMessage.request.id}>
                                                    {thirdPartyMessage.request.processId}
                                                </Link>
                                            ) : (
                                                thirdPartyMessage.request.processId
                                            )}{' '}
                                            {thirdPartyMessage.request.service.code}
                                        </>
                                    )}
                                </TableCell>
                                <TableCell>{thirdPartyMessage.messageType}</TableCell>
                                <TableCell>{thirdPartyMessage.messageSubtype}</TableCell>
                                <TableCell>
                                    {translateKey(`processThirdPartyMessagesEnum.${thirdPartyMessage.status}`)}
                                </TableCell>
                                <TableCell>{thirdPartyMessage.recipient}</TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={thirdPartyMessage.firstScheduledDateTime} />
                                </TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={thirdPartyMessage.nextScheduledDateTime} />
                                </TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={thirdPartyMessage.finishedDateTime} />
                                </TableCell>
                                <TableCell>{thirdPartyMessage.numberOfFailures}</TableCell>
                                <TableCell>
                                    <LinkOnClick
                                        onClick={() => {
                                            setResultsMessageId(thirdPartyMessage.id);
                                        }}
                                    >
                                        {translateKey('processThirdPartyMessages.viewResults')}
                                    </LinkOnClick>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {resultsMessageId && (
                <ThirdPartyMessageResultModal
                    messageId={resultsMessageId}
                    onClose={() => setResultsMessageId(undefined)}
                />
            )}
            <ListPager
                isDisabled={isSearching}
                itemsPerPage={searchState.itemsPerPage}
                onChangeItemsPerPage={(itemsPerPage) => {
                    handleSearch({ itemsPerPage, page: 1 });
                }}
                onChangePage={async (page) => handleSearch({ page })}
                page={searchState.page}
                total={totalMessages}
            />
        </PageTemplate>
    );

    async function loadMessages() {
        setIsSearching(true);
        const url = buildMessageUrl();
        const body = buildBody();
        const result = await fetchJson(url, 'POST', body);
        setThirdPartyMessages(result.json.items);
        setTotalMessages(result.json.total);
        setIsSearching(false);
    }

    function buildMessageUrl() {
        const urlBuilder = new UrlBuilder('/api/thirdPartyMessages');

        return urlBuilder.add('page', searchState.page).add('limit', searchState.itemsPerPage).build();
    }

    function buildBody() {
        const body: IGetThirdPartyMessages = {};
        if (searchState.status) {
            body.status = searchState.status.value as ThirdPartyMessageStatus;
        }
        if (searchState.messageType) {
            body.messageType = searchState.messageType.value as ThirdPartyMessageType;
        }
        if (searchState.messageSubtype) {
            body.messageSubtype = searchState.messageSubtype;
        }
        if (searchState.firstScheduledDateTime) {
            body.firstScheduledDateTime = formatDateIntoString(searchState.firstScheduledDateTime);
        }

        return body;
    }

    function clearSearchState() {
        const cleanSearchState = { ...getCleanThirdPartyMessagesSearchState(), itemsPerPage: searchState.itemsPerPage };
        setSearchState(cleanSearchState);
        pushSearchStateToHistory(cleanSearchState);
    }

    function handleSearch(incomingSearchState: Partial<IThirdPartyMessagesListSearchState>) {
        const newSearchState = { ...searchState, ...incomingSearchState };
        setSearchState(newSearchState);
        pushSearchStateToHistory(newSearchState);
    }
}

export function getCleanThirdPartyMessagesSearchState() {
    return {
        firstScheduledDateTime: null,
        itemsPerPage: 10,
        messageSubtype: '',
        messageType: null,
        page: 1,
        status: null,
    };
}
